import type {
	CompositeFilterDescriptor,
	FilterDescriptor,
} from "@tql/ui-components-react/kendo/data-query";

const isCompositeFilterDescriptor = (
	descriptor: FilterDescriptor | CompositeFilterDescriptor,
): descriptor is CompositeFilterDescriptor =>
	"filters" in descriptor && "logic" in descriptor;

const hasMeaningfulValue = (value: unknown): boolean => {
	if (value === null || value === undefined) return false;
	if (typeof value === "string") return value.trim().length > 0;
	if (typeof value === "number") return Number.isFinite(value);
	return true;
};

const pruneDescriptor = (
	descriptor: FilterDescriptor | CompositeFilterDescriptor,
): FilterDescriptor | CompositeFilterDescriptor | null => {
	if (isCompositeFilterDescriptor(descriptor)) {
		const children = descriptor.filters
			.map((child) => pruneDescriptor(child))
			.filter(
				(d): d is FilterDescriptor | CompositeFilterDescriptor => d !== null,
			);
		if (children.length === 0) return null;
		return { ...descriptor, filters: children };
	}
	return hasMeaningfulValue(descriptor.value) ? descriptor : null;
};

/**
 * Walks a Kendo `CompositeFilterDescriptor` tree and removes leaf
 * descriptors whose `value` is null/undefined or an empty/whitespace
 * string. Useful when handling Kendo grid filter-change events: the
 * default text filter cell does **not** remove its descriptor when the
 * user clears the input — it just sets `value` to null/empty. Left in
 * the tree, those empty descriptors cause `contains(...)` checks to
 * exclude rows whose target field is null/undefined, so the user
 * appears to "lose" data after clearing a filter.
 *
 * Always returns a composite descriptor with the same `logic` as the
 * input; if every leaf is empty the returned descriptor has an empty
 * `filters` array (equivalent to "no filter applied").
 */
export const pruneEmptyFilters = (
	filter: CompositeFilterDescriptor | null | undefined,
): CompositeFilterDescriptor => {
	if (!filter) return { logic: "and", filters: [] };
	const pruned = pruneDescriptor(filter);
	if (pruned === null || !isCompositeFilterDescriptor(pruned)) {
		return { logic: filter.logic, filters: [] };
	}
	return pruned;
};
